/**
 * Single source of truth for slide durations, trim and transition overlaps.
 *
 * Used by the renderer normalizers, `Root.tsx` `calculateMetadata`, the Remotion
 * compositions and the (vendored) frontend editor, so preview duration always
 * equals render duration. Must stay free of Node imports.
 */
import {TRANSITION_NAMES} from '../types';
import type {ReelSlide, TransitionName} from '../types';
import {parseDuration} from './values';

export const DEFAULT_FPS = 30;
export const DEFAULT_ITEM_DURATION_SECONDS = 3;
export const MIN_TRANSITION_DURATION_SECONDS = 0.1;
export const DEFAULT_TRANSITION_DURATION_SECONDS = 0.3;
export const SLOW_TRANSITION_DURATION_SECONDS = 0.5;
export const ZOOM_PUNCH_TRANSITION_DURATION_SECONDS = 0.4;

export interface TimelineEntry {
  slide: ReelSlide;
  fromFrame: number;
  durationInFrames: number;
  transitionOut?: {name: TransitionName; durationInFrames: number};
}

export interface Timeline {
  entries: TimelineEntry[];
  totalDurationInFrames: number;
}

type SlideDurationSource = Pick<ReelSlide, 'mediaType' | 'duration' | 'trimStartSeconds' | 'trimEndSeconds'>;

/**
 * An inverted pair (`end <= start`) is unusable — neither bound may be applied, or the
 * video would get a nonsensical `trimBefore`/`trimAfter` range. The editor can produce
 * such a pair mid-drag, so both the normalizers and `SlideMedia` guard on this.
 */
export const hasInvertedTrim = (start: number | undefined, end: number | undefined): boolean => {
  if (typeof start !== 'number' || typeof end !== 'number') {
    return false;
  }
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return false;
  }
  return end <= start;
};

/** Declared duration wins; a video with a usable trim range derives it; otherwise the default. */
export const effectiveDurationSeconds = (slide: SlideDurationSource): number => {
  const declared = parseDuration(slide.duration);
  if (declared > 0) {
    return declared;
  }

  if (slide.mediaType === 'video') {
    const trimmed = trimmedDurationSeconds(slide.trimStartSeconds, slide.trimEndSeconds);
    if (trimmed > 0) {
      return trimmed;
    }
  }

  return DEFAULT_ITEM_DURATION_SECONDS;
};

const SUPPORTED_TRANSITIONS: ReadonlySet<string> = new Set(TRANSITION_NAMES);

/** Whitelist guard for the `transition_out` enum — unknown values are dropped, not rendered. */
export const isTransitionName = (value: unknown): value is TransitionName =>
  typeof value === 'string' && SUPPORTED_TRANSITIONS.has(value);

/**
 * Overlap lengths that differ from the short default. Named one by one rather than
 * derived from the name: `zoom-punch` needs room for its push-in and back-out but
 * carries no `slow` prefix, so a prefix rule would silently give it 0.3s.
 */
const TRANSITION_DURATION_OVERRIDES: Partial<Record<TransitionName, number>> = {
  'slow-zoom-in': SLOW_TRANSITION_DURATION_SECONDS,
  'slow-zoom-out': SLOW_TRANSITION_DURATION_SECONDS,
  'zoom-punch': ZOOM_PUNCH_TRANSITION_DURATION_SECONDS,
};

/**
 * Overlap length a slide gets when it declares no explicit `transition_duration`.
 * Everything not listed above — fade, the plain zooms, the Remotion built-ins —
 * shares the same short default.
 */
export const defaultTransitionDurationSeconds = (transition: TransitionName): number =>
  TRANSITION_DURATION_OVERRIDES[transition] ?? DEFAULT_TRANSITION_DURATION_SECONDS;

export const computeTimeline = (slides: ReelSlide[], fps: number): Timeline => {
  const safeFps = Number.isFinite(fps) && fps > 0 ? fps : DEFAULT_FPS;
  const seconds = slides.map((slide) => effectiveDurationSeconds(slide));
  const frames = seconds.map((value) => Math.max(1, Math.round(value * safeFps)));

  const entries: TimelineEntry[] = [];
  let fromFrame = 0;

  slides.forEach((slide, index) => {
    const durationInFrames = frames[index];
    const next = slides[index + 1];
    // The last slide's transition has nothing to overlap into and is ignored.
    const transitionFrames = next
      ? gapInFrames(slide, seconds[index], seconds[index + 1], durationInFrames, frames[index + 1], safeFps)
      : 0;

    entries.push({
      slide,
      fromFrame,
      durationInFrames,
      ...(slide.transition && transitionFrames > 0
        ? {transitionOut: {name: slide.transition, durationInFrames: transitionFrames}}
        : {}),
    });

    fromFrame += durationInFrames - transitionFrames;
  });

  const totalDurationInFrames = entries.reduce(
    (sum, entry) => sum + entry.durationInFrames - (entry.transitionOut?.durationInFrames ?? 0),
    0,
  );

  return {entries, totalDurationInFrames: Math.max(1, totalDurationInFrames)};
};

const trimmedDurationSeconds = (start: number | undefined, end: number | undefined): number => {
  if (typeof start !== 'number' || typeof end !== 'number') {
    return 0;
  }
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return 0;
  }
  return end - start;
};

const gapInFrames = (
  slide: ReelSlide,
  currentSeconds: number,
  nextSeconds: number,
  currentFrames: number,
  nextFrames: number,
  fps: number,
): number => {
  if (!slide.transition) {
    return 0;
  }

  const requested =
    slide.transitionDurationSeconds !== undefined && slide.transitionDurationSeconds > 0
      ? slide.transitionDurationSeconds
      : defaultTransitionDurationSeconds(slide.transition);
  const maxSeconds = Math.min(currentSeconds, nextSeconds) / 2 - 1 / fps;
  if (maxSeconds <= 0) {
    return 0;
  }

  const clamped = Math.min(Math.max(requested, MIN_TRANSITION_DURATION_SECONDS), maxSeconds);
  // Keep the TransitionSeries invariant (a sequence is never shorter than an adjacent transition).
  const gap = Math.min(Math.round(clamped * fps), Math.min(currentFrames, nextFrames) - 1);
  return gap > 0 ? gap : 0;
};
